import { useEffect, useState } from "react"

const CONSENT_EVENTS = [
  "CookiebotOnConsentReady",
  "CookiebotOnAccept",
  "CookiebotOnDecline",
]

const readConsent = (category) => {
  if (typeof window === "undefined") {
    return false
  }
  return Boolean(window.Cookiebot?.consent?.[category])
}

// A stored decision exists once Cookiebot sets a non-zero consent stamp.
const hasDecision = () =>
  typeof window !== "undefined" &&
  !!window.Cookiebot?.consent &&
  !!window.Cookiebot.consent.stamp &&
  window.Cookiebot.consent.stamp !== "0"

/**
 * Returns the current Cookiebot consent (boolean) for a single category
 * (`necessary` | `preferences` | `statistics` | `marketing`) and re-renders
 * whenever the visitor changes their consent. SSR-safe: returns `false`
 * during server render where `window` is undefined.
 *
 * On reload Cookiebot restores a stored decision and fires
 * `CookiebotOnConsentReady` very early — often *before* React hydrates and
 * this effect attaches its listeners, so the event is missed and the gate
 * would stay closed forever. We therefore also poll briefly until a decision
 * is present, then sync.
 *
 * The state MUST initialise to `false` (never read the live consent in the
 * initialiser): the server always renders "no consent", and when Cookiebot
 * restores a stored decision before hydration a `true` first render would
 * mismatch the SSR markup. React 18 then silently adopts the SSR DOM without
 * patching attributes, so the gate's classes/styles stay on screen while the
 * children mount inside the gate container (e.g. the Standorte map collapsed
 * to 0 height behind the consent poster). Consent is read after mount, which
 * swaps the DOM in a proper re-render.
 */
const useCookiebotConsent = (category) => {
  const [consented, setConsented] = useState(false)

  useEffect(() => {
    let active = true
    const update = () => {
      if (active) setConsented(readConsent(category))
    }

    // Sync once on mount in case consent was already set before this effect ran.
    update()

    CONSENT_EVENTS.forEach((event) => window.addEventListener(event, update))

    // Catch the reload race: a restored decision (and CookiebotOnConsentReady)
    // may have landed before the listeners above attached. Poll until a
    // decision is present, then stop (bounded ~6s safety net).
    let ticks = 0
    const poll = setInterval(() => {
      update()
      if (!active || hasDecision() || (ticks += 1) >= 30) {
        clearInterval(poll)
      }
    }, 200)

    return () => {
      active = false
      clearInterval(poll)
      CONSENT_EVENTS.forEach((event) =>
        window.removeEventListener(event, update)
      )
    }
  }, [category])

  return consented
}

export { useCookiebotConsent }
